import React, { useEffect, useRef, useState } from "react";
import { MainLayout } from "./layouts/MainLayout";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BookingProvider } from "./context/BookingContext";
import { createChatSocket } from "./services/socketClient";

import { DashboardPage } from "./pages/DashboardPage";
import { BookingManagementPage } from "./pages/BookingManagementPage";
import { MeetingSchedulePage } from "./pages/MeetingSchedulePage";
import { CaseFilesPage } from "./pages/CaseFilesPage";
import { PaymentsPage } from "./pages/PaymentsPage";
import { ChatPage } from "./pages/ChatPage";
import { AIHelpPage } from "./pages/AIHelpPage";
import { DemandDraftPage } from "./pages/DemandDraftPage";
import { SettingsPage } from "./pages/SettingsPage";
import { NotificationPage } from "./pages/NotificationPage";
import { LawyerLoginPage } from "./pages/LawyerLoginPage";
import { ClientRegisterPage } from "./pages/ClientRegisterPage";
import { GoogleCompleteProfilePage } from "./pages/GoogleCompleteProfilePage";

const DEFAULT_PAGE = "dashboard";
const COMPLETE_PROFILE_BANNER = "Complete your profile to access dashboard";

const PAGE_TO_PATH = {
  dashboard:       "/lawyer/dashboard",
  bookings:        "/lawyer/bookings",
  schedule:        "/lawyer/schedule",
  cases:           "/lawyer/cases",
  payments:        "/lawyer/payments",
  chat:            "/lawyer/chat",
  "ai-help":       "/lawyer/ai-help",
  "demand-draft":  "/lawyer/demand-draft",
  notifications:   "/lawyer/notifications",
  settings:        "/lawyer/settings",
};

const PATH_TO_PAGE = Object.entries(PAGE_TO_PATH).reduce((acc, [page, path]) => {
  acc[path] = page;
  return acc;
}, {});

const resolvePageFromPath = (pathname) => {
  if (!pathname || pathname === "/") return DEFAULT_PAGE;
  return PATH_TO_PAGE[pathname] || DEFAULT_PAGE;
};

const resolvePathFromPage = (page) => PAGE_TO_PATH[page] || PAGE_TO_PATH[DEFAULT_PAGE];

const AppContent = () => {
  const {
    isAuthenticated,
    isBootstrapping,
    login,
    loginWithGoogle,
    logout,
    requestOtp,
    register,
    confirmOtp,
    registerWithGoogle,
    completeGoogleSignup,
    user,
  } = useAuth();

  const [currentPage, setCurrentPage] = useState(() => resolvePageFromPath(window.location.pathname));
  const [navigationState, setNavigationState] = useState(null);
  const [authView, setAuthView] = useState("login");
  const [pendingGoogleProfile, setPendingGoogleProfile] = useState(null);
  const socketRef = useRef(null);

  // NOTE: Profile completion guard disabled for testing — re-enable before production
  // const needsProfileCompletion = useMemo(
  //   () =>
  //     Boolean(
  //       isAuthenticated &&
  //         String(user?.role || "").toLowerCase() === "lawyer" &&
  //         user?.profileComplete === false
  //     ),
  //   [isAuthenticated, user?.profileComplete, user?.role]
  // );
  const needsProfileCompletion = false;

  useEffect(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    if (!isAuthenticated) {
      return undefined;
    }

    const token = localStorage.getItem("lawyerAuthToken");
    if (!token) {
      return undefined;
    }

    const socket = createChatSocket(token);
    socketRef.current = socket;
    const lawyerProfileId = String(user?.lawyerProfileId || "").trim();

    const joinLawyerRoom = () => {
      if (!lawyerProfileId) return;
      socket.emit("joinLawyerRoom", lawyerProfileId);
    };

    const handleAvailabilityUpdated = (data) => {
      if (typeof window === "undefined") return;
      window.dispatchEvent(new CustomEvent("lawyer:availability-updated", { detail: data }));
    };

    const handleBookingUpdated = (data) => {
      if (typeof window === "undefined") return;
      window.dispatchEvent(new CustomEvent("lawyer:booking-updated", { detail: data }));
    };

    socket.on("connect", joinLawyerRoom);
    socket.on("bookingUpdated", handleBookingUpdated);

    socket.on("availabilityUpdated", handleAvailabilityUpdated);

    if (socket.connected) {
      joinLawyerRoom();
    }

    return () => {
      socket.off("connect", joinLawyerRoom);
      socket.off("bookingUpdated", handleBookingUpdated);
      socket.off("availabilityUpdated", handleAvailabilityUpdated);
      socket.disconnect();
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    };
  }, [isAuthenticated, user?.lawyerProfileId]);

  useEffect(() => {
    if (!isAuthenticated) return;

    if (needsProfileCompletion) {
      if (currentPage !== "settings") {
        setCurrentPage("settings");
        setNavigationState(null);
      }
      if (window.location.pathname !== "/lawyer/settings") {
        window.history.replaceState({}, "", "/lawyer/settings");
      }
      return;
    }

    const targetPath = resolvePathFromPage(currentPage);
    if (window.location.pathname !== targetPath) {
      window.history.replaceState({}, "", targetPath);
    }
  }, [isAuthenticated, needsProfileCompletion, currentPage]);

  useEffect(() => {
    const handlePopState = () => {
      const requestedPage = resolvePageFromPath(window.location.pathname);

      if (needsProfileCompletion && requestedPage !== "settings") {
        setCurrentPage("settings");
        setNavigationState(null);
        window.history.replaceState({}, "", "/lawyer/settings");
        return;
      }

      setCurrentPage(requestedPage);
      setNavigationState(null);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [needsProfileCompletion]);

  const handleNavigate = (page, state = null) => {
    if (needsProfileCompletion && page !== "settings") {
      setCurrentPage("settings");
      setNavigationState(null);
      if (window.location.pathname !== "/lawyer/settings") {
        window.history.replaceState({}, "", "/lawyer/settings");
      }
      return;
    }

    setCurrentPage(page);
    setNavigationState(state);

    const targetPath = resolvePathFromPage(page);
    if (window.location.pathname !== targetPath) {
      window.history.pushState({}, "", targetPath);
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLogout = () => {
    logout();
    setAuthView("login");
    setCurrentPage(DEFAULT_PAGE);
    setNavigationState(null);
  };

  const pageProps = {
    onNavigate: handleNavigate,
    onLogout: handleLogout,
    navState: navigationState,
  };

  const renderPage = () => {
    switch (currentPage) {
      case "dashboard":
        return <DashboardPage {...pageProps} />;
      case "bookings":
        return <BookingManagementPage {...pageProps} />;
      case "schedule":
        return <MeetingSchedulePage {...pageProps} />;
      case "cases":
        return <CaseFilesPage {...pageProps} />;
      case "payments":
        return <PaymentsPage {...pageProps} />;
      case "chat":
        return <ChatPage {...pageProps} />;
      case "ai-help":
        return <AIHelpPage {...pageProps} />;
      case "demand-draft":
        return <DemandDraftPage {...pageProps} />;
      case "settings":
        return <SettingsPage {...pageProps} />;
      case "notifications":
        return <NotificationPage {...pageProps} />;
      default:
        return <DashboardPage {...pageProps} />;
    }
  };

  const resolveLandingPage = (nextUser) => {
    if (String(nextUser?.role || "").toLowerCase() !== "lawyer") {
      return DEFAULT_PAGE;
    }
    if (nextUser?.profileComplete === false) return "settings";
    return DEFAULT_PAGE;
  };

  if (isBootstrapping) {
    return (
      <div className="min-h-screen bg-[#f1f4f9] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    const currentPath = window.location.pathname;
    const isBaseUrl = currentPath === "/lawyer" || currentPath === "/lawyer/" || currentPath === "/lawyer/dashboard" || currentPath === "/" || currentPath === "/dashboard" || currentPath === "/login";
    const redirectParam = isBaseUrl ? "" : `&redirect=${encodeURIComponent(currentPath)}`;
    
    let loginUrl = "/login";
    if (window.location.hostname.includes("lawyer-staging.findmylawyer.today")) {
      loginUrl = "https://staging.findmylawyer.today/login";
    } else if (window.location.hostname.includes("lawyer.findmylawyer.today")) {
      loginUrl = "https://findmylawyer.today/login";
    } else if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
      loginUrl = "http://localhost:5173/login";
    }

    window.location.href = `${loginUrl}?role=lawyer${redirectParam}`;
    return null;
  }

  return (
    <MainLayout
      currentPage={currentPage}
      onNavigate={handleNavigate}
      onLogout={handleLogout}
      user={user}
      socket={socketRef.current}
      profileGuardActive={needsProfileCompletion}
      profileGuardMessage={needsProfileCompletion ? COMPLETE_PROFILE_BANNER : ""}
      profileGuardAllowedPage={needsProfileCompletion ? "settings" : null}
    >
      {renderPage()}
    </MainLayout>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <BookingProvider>
        <AppContent />
      </BookingProvider>
    </AuthProvider>
  );
};

export default App;

